
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Notification } from "./useNotificationDetail";
import { AIAnalysisResult, NotificationProductData } from "@/types/notifications";
import { updateNotificationInDb } from "@/services/notificationUpdateService";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// UUID validation helper
const isValidUUID = (str: string | undefined): boolean => {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const useNotificationUpdate = (
  id: string | undefined,
  notification: Notification | undefined,
  editedNotification: Notification | null,
  setEditedNotification: React.Dispatch<React.SetStateAction<Notification | null>>,
  setEditMode: (mode: boolean) => void
) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const updateNotification = useMutation({
    mutationFn: async (updatedData: Partial<Notification>) => {
      if (!id || !notification) throw new Error("Invalid notification data");
      
      // Only include fields that have actually changed
      const changedFields: Partial<Notification> = {};
      
      // Compare and only include changed fields
      Object.keys(updatedData).forEach(key => {
        const typedKey = key as keyof Notification;
        // Make sure the field exists in both objects before comparing
        if (updatedData[typedKey] !== notification[typedKey]) {
          // Type assertion to ensure TypeScript understands this is a valid key for changedFields
          (changedFields[typedKey] as any) = updatedData[typedKey];
        }
      });
      
      console.log("Sending changed fields to update:", changedFields);
      
      // If there are no changes, return the original notification
      if (Object.keys(changedFields).length === 0) {
        console.log("No changes detected, returning original notification");
        return notification;
      }
      
      return updateNotificationInDb(id, notification, changedFields, user?.id, user?.name);
    },
    onSuccess: (updatedNotification) => {
      queryClient.setQueryData(['notification', id], updatedNotification);
      setEditedNotification(updatedNotification as Notification);
      setEditMode(false);
      toast.success("Melding bijgewerkt");
    },
    onError: (error: any) => {
      console.error("Error updating notification:", error);
      toast.error("Fout bij het bijwerken van de melding", { 
        description: error.message 
      });
    }
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (editedNotification) {
      setEditedNotification({
        ...editedNotification,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editedNotification) {
      try {
        console.log("Submitting edited notification:", editedNotification);
        await updateNotification.mutateAsync(editedNotification);
        
        // Ensure we have a valid notification ID
        const notificationId = notification?.id || editedNotification?.id || id;
        
        if (!notificationId) {
          console.error("No notification ID available for webhook");
          toast.warning("Melding opgeslagen, maar webhook kon niet verzonden worden (geen ID)");
          return;
        }

        console.log("Calling send-voorraadverschil-webhook with ID:", notificationId);
        
        // Only send triggered_by if it's a valid UUID
        const body: any = {
          notification_id: notificationId,
        };
        
        if (isValidUUID(user?.id)) {
          body.triggered_by = user?.id;
          body.triggered_by_name = user?.name;
        }
        
        // Call edge function to send voorraadverschil webhook
        const { data, error } = await supabase.functions.invoke('send-voorraadverschil-webhook', {
          body
        });
        
        console.log("Webhook response:", { data, error, statusCode: data?.status });
        
        if (error) {
          console.error("Webhook invocation error:", error);
          toast.warning("Melding opgeslagen, maar webhook verzending mislukt", {
            description: error.message
          });
        } else if (!data?.success) {
          console.error("Webhook returned unsuccessful response:", data);
          toast.warning("Melding opgeslagen, maar webhook verzending mislukt", {
            description: data?.body ? `${data.body.substring(0, 120)}` : `Status: ${data?.status}`
          });
        } else {
          toast.success("Melding opgeslagen en webhook verzonden");
        }
        
        // Invalidate webhook logs to refresh UI
        queryClient.invalidateQueries({ queryKey: ['webhook-logs', notificationId] });
      } catch (error: any) {
        console.error("Error in handleSubmit:", error);
        toast.error("Fout bij opslaan", {
          description: error.message
        });
      }
    }
  };

  const resetNotification = () => {
    if (notification) {
      setEditedNotification(notification);
    }
    setEditMode(false);
  };

  const handleAIAnalysisComplete = (data: AIAnalysisResult) => {
    if (editedNotification) {
      setEditedNotification(prev => ({
        ...prev!,
        message: data.formattedText || prev!.message,
        vo_number: data.voNumber || prev!.vo_number
      }));
    }
  };

  const handleProductsChange = (products: NotificationProductData[]) => {
    if (editedNotification) {
      setEditedNotification(prev => ({
        ...prev!,
        products
      }));
    }
  };

  return {
    updateNotification,
    handleInputChange,
    handleSubmit,
    resetNotification,
    handleAIAnalysisComplete,
    handleProductsChange
  };
};
